/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUpdatedCodes, getMigrationStatus, updateMigrationStatus } from '../../../../../state/thunks/commenting';
// utils
// material
import { Button, CopySnippetField, InlineMessage, displayModal } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from '../../../../../material/expandablesection/expandablesection';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription';
import LoadingState from '../../../../../material/states/loading/loading';
// content
import MigrationConfirm from './migrationconfirm/migrationconfirm';
// styles
import injectSheet from "react-jss";
import styles from './advanced_styles.js';

/* ========== ~~~~~~~~~~ COMMENTING ADVANCED: ADVANCED SECTION ~~~~~~~~~~ ========== */
const CommentingAdvancedSection = injectSheet(styles)((props) => {

  const dispatch = useDispatch()
  const commentingUnitUUID = useSelector(state => state.commenting.commentingConfig?.data?.commentingUnitUUID)
  const isLoading = useSelector(state => state.commenting.commentingAdvanced.isFetching)
  const isError = useSelector(state => state.commenting.commentingAdvanced.isError)
  const status = useSelector(state => state.commenting.commentingAdvanced.migrationStatus)
  const { siteUUID } = useSelector((state) => state.session)
  const codes = useSelector(state => state.commenting.commentingAdvanced.codes)

  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if(siteUUID) dispatch(getUpdatedCodes(siteUUID))
  }, []);

  useEffect(() => {
    if(commentingUnitUUID) dispatch(getMigrationStatus(commentingUnitUUID))
  }, [])

  const showMigrationConfirm = () => dispatch(displayModal(true, <MigrationConfirm commentingUnitUUID={commentingUnitUUID} />)) 

  const isMigrationPending = status !== 'COMPLETED' && status !== 'NOT_REQUIRED'
  // if (isLoading && !status) return null
  // if (isError) return <span className='error'>Error fetching migration status</span>

  const migrationDesc = status === 'READY' ? 'Now that the client has integrated the new codes on their website and the changes are live, click “Start migration” to begin.' : 'Migrate data to new commenting unit for this publisher'

  const migrationNote = () => {
    if(status === 'READY') return 'This can only be done once! Make sure the new code is live on the page before clicking the “Start migration” button.'
    if(status === 'STARTED') return 'Migration process has started'
    if(status === 'COMPLETED') return 'A previous migration has been completed'
  }
  
  const handleToggle = () => setIsOpened(!isOpened) 

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleToggle}
      isOpened={isOpened}
      configTitle={'View'}
      cancelTitle={'Close'}
      title='Code Migration'
    />
  );
  
  return (
    <ExpandableSection
      className={`commentingadvancedsection ${props.classes.commentingadvancedsection}`}
      controller={controller}
      isOpened={isOpened}
      handleCancel={handleToggle}
      cancelTitle={'Close'}
    >
    {isOpened && 
    <>
    { (isLoading || !status) ? <SectionCard klass={'loadingsection'} ><LoadingState /></SectionCard> :
    <div>
    { status === 'READY' && 
    <SectionCard>
      <div className={'directions'}>
        <SectionGroup>
          <SectionGroupDescription
            title={'Directions'}
            description={'Occasionally new features are added to the commenting product that may require the client to update their integration codes. Engineering will directly reach out to notify account team members if this should occur. These instances are rare, but if they occur, the following steps need to be followed:'}
          />
          <ol className={'migrationsteps'}>
            <li>Have the client copy and paste the new codes (shown below) into their websites. Ensure they remove the old codes and replace with the new codes.</li>
            <li>Once the new code is set live on their site, hit the “Migrate Now” button. Ensure you only hit this button when the code is live.</li>
          </ol>
          <InlineMessage
            key={'meow'}
            display={true}
            name={'migrationmessage'}
            variant={'system'}
            icon={'exclamation-triangle'}
            title={null}
            message={<span>The migration may take time to complete and will show a status change when finished.</span>}
            close={false}
          />
        </SectionGroup>
      </div>
    </SectionCard>
    }
    { status === 'READY' &&
    <SectionCard>
      <div className={`advanced ${props.classes.advanced}`}>
          <div className='code'>
            <SectionGroupDescription
              title={'Codes'}
              description={''}
            />

            <CopySnippetField
              title={'Header Code'}
              description={<div>Place the following code right after the <strong>{`<head>`}</strong> tag in your HTML source code.</div>}
              codeSnippet={codes.commentingUnitCode.headerCode}
            />

            <CopySnippetField
              title={'Commenting Unit Code'}
              description={
                <div>Place the Commenting Unit code inside of the <strong>{`<body>`}</strong> tag in your HTML source code where you want the Commenting Unit to appear in your site.
                </div>
              }
              codeSnippet={codes.commentingUnitCode.bodyCode}
            />

            <CopySnippetField
              title={'Community Add-On Code'}
              description={
                <div>
                  Place the Community Add-On code inside of the <strong>&lt;body&gt;</strong> tag
                  in your HTML source code where you want the Community Add-On to appear in your site.
                </div>
              }
              codeSnippet={codes.communityCode.code}
            />

            <CopySnippetField
              title={'Comments Counter Loading Script'}
              description={
                <div>
                  Add the following script to the bottom of the page where you’d like to display the Comments Counter,
                  right before the closing <strong>{'</body>'}</strong>:
                </div>
              }
              codeSnippet={codes.commentsCounterCode.bodyCode}
            />
          </div>
      </div>
    </SectionCard>
    }
    <SectionCard>
      <div className='migration'>
        <SectionGroupDescription
          title={'Migration'}
          description={migrationDesc}
        />
        { status !== 'NOT_REQUIRED' &&
        <InlineMessage
          key={'meow'}
          display={true}
          name={'migrationmessage'}
          variant={'system'}
          icon={'exclamation-triangle'}
          title={null}
          message={<span>{migrationNote()}</span>}
          close={false}
        />
        }
        <div className={`status notrequired-${status === 'NOT_REQUIRED'}`}>Status: {status}</div>
        <Button
          disabled={status !== 'READY' || isLoading}
          type={'action'}
          variant={'primary'}
          text={'Start migration'}
          operation={'link'}
          behavior={'none'}
          icon={'random'}
          iconRight={true}
          handleClick={showMigrationConfirm}
        />
        { status === 'NOT_REQUIRED' &&
        <InlineMessage
          key={'meow'}
          display={true}
          name={'notrequiredmsg'}
          variant={'system'}
          icon={'exclamation-triangle'}
          title={null}
          message={<span>Migration is not required</span>}
          close={false}
        />
        }
      </div>
    </SectionCard>
    </div>
    }
    </>
    }
    </ExpandableSection>
  );
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CommentingAdvancedSection